import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { refreshAccessToken, logoutUser } from '../utils/api';
import { APP_CONFIG } from '../config/auth-config';

const CustomerContext = createContext();

export const useCustomer = () => useContext(CustomerContext);

const { IDLE_TIMEOUT, TOKEN_REFRESH_INTERVAL, LOG_INTERVAL } = APP_CONFIG;

export const CustomerProvider = ({ children, navigate }) => {
  const [customerId, setCustomerId] = useState(null);
  const [customerEmail, setCustomerEmail] = useState(null);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshTokenValue, setRefreshTokenValue] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [sessionTimeout, setSessionTimeout] = useState(null);

  const updateCustomerId = useCallback((id) => {
    setCustomerId(id);
    localStorage.setItem('customerId', id);
  }, []);

  const updateCustomerEmail = useCallback((email) => {
    setCustomerEmail(email);
    localStorage.setItem('customerEmail', email);
  }, []);

  const updateCustomerProfile = useCallback((profile) => {
    setCustomerProfile(profile);
    localStorage.setItem('customerProfile', JSON.stringify(profile));
  }, []);

  const updateToken = useCallback((newToken, newRefreshToken, newExpiresAt) => {
    setToken(newToken);
    setRefreshTokenValue(newRefreshToken);
    setExpiresAt(new Date(newExpiresAt));
    localStorage.setItem('token', newToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    localStorage.setItem('expiresAt', newExpiresAt);
  }, []);

  const clearLocalStorage = useCallback(() => {
    localStorage.removeItem('customerId');
    localStorage.removeItem('customerEmail');
    localStorage.removeItem('customerProfile');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expiresAt');
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      setCustomerId(null);
      setCustomerEmail(null);
      setCustomerProfile(null);
      setToken(null);
      setRefreshTokenValue(null);
      setExpiresAt(null);
      setSessionTimeout(null);

      clearLocalStorage();
      if (navigate) {
        navigate('/login');
      }
    }
  }, [navigate, clearLocalStorage]);

  useEffect(() => {
    const storedCustomerId = localStorage.getItem('customerId');
    const storedCustomerEmail = localStorage.getItem('customerEmail');
    const storedCustomerProfile = localStorage.getItem('customerProfile');
    const storedToken = localStorage.getItem('token');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    const storedExpiresAt = localStorage.getItem('expiresAt');

    if (storedCustomerId) setCustomerId(storedCustomerId);
    if (storedCustomerEmail) setCustomerEmail(storedCustomerEmail);
    if (storedCustomerProfile) setCustomerProfile(JSON.parse(storedCustomerProfile));
    if (storedToken) setToken(storedToken);
    if (storedRefreshToken) setRefreshTokenValue(storedRefreshToken);
    if (storedExpiresAt) setExpiresAt(new Date(storedExpiresAt));
  }, []);

  useEffect(() => {
    let sessionTimeout;
    let idleTimeout;
    let tokenRefreshInterval;
    let logInterval;

    const resetIdleTimeout = () => {
      clearTimeout(idleTimeout);
      idleTimeout = setTimeout(() => {
        console.log(`User idle for ${IDLE_TIMEOUT / 1000} seconds, logging out`);
        logout();
      }, IDLE_TIMEOUT);
    };

    const refreshTokenAndReset = async () => {
      try {
        const newToken = await refreshAccessToken();
        updateToken(newToken, refreshTokenValue, new Date(Date.now() + APP_CONFIG.jwtExpiresIn * 1000));
        resetIdleTimeout();
      } catch (error) {
        console.error('Failed to refresh token:', error);
        logout();
      }
    };

    const logTimeUntilExpiry = () => {
      if (expiresAt) {
        const timeUntilExpiry = new Date(expiresAt) - new Date();
        console.log(`Time until session expiry: ${(timeUntilExpiry / 1000).toFixed(2)} seconds`);
      }
    };

    if (token && expiresAt) {
      const timeUntilExpiry = new Date(expiresAt) - new Date();
      
      if (timeUntilExpiry > 0) {
        console.log(`Initial session timeout set to ${(timeUntilExpiry / 1000).toFixed(2)} seconds`);
        sessionTimeout = setTimeout(() => {
          console.log('Session timeout, logging out');
          logout();
        }, timeUntilExpiry);

        console.log(`Current time: ${new Date().toISOString()}`, `Token will refresh in ${TOKEN_REFRESH_INTERVAL / 1000} seconds`);
        tokenRefreshInterval = setInterval(refreshTokenAndReset, TOKEN_REFRESH_INTERVAL);

        console.log(`Idle timeout set to ${IDLE_TIMEOUT / 1000} seconds`);
        resetIdleTimeout();
        window.addEventListener('mousemove', resetIdleTimeout);
        window.addEventListener('keypress', resetIdleTimeout);

        logInterval = setInterval(logTimeUntilExpiry, LOG_INTERVAL);
      } else {
        console.log('Token already expired, logging out');
        logout();
      }
    }

    return () => {
      clearTimeout(sessionTimeout);
      clearTimeout(idleTimeout);
      clearInterval(tokenRefreshInterval);
      clearInterval(logInterval);
      window.removeEventListener('mousemove', resetIdleTimeout);
      window.removeEventListener('keypress', resetIdleTimeout);
    };
  }, [token, expiresAt, refreshTokenValue, logout, updateToken]);

  return (
    <CustomerContext.Provider value={{ 
      customerId, 
      updateCustomerId, 
      customerEmail, 
      updateCustomerEmail, 
      customerProfile, 
      updateCustomerProfile,
      token,
      updateToken,
      logout
    }}>
      {children}
    </CustomerContext.Provider>
  );
};